import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, Activity, ChevronRight, Calendar, Pencil, ImagePlus } from "lucide-react";
import { sportsApi, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, SectionTitle, EmptyState, UserAvatar, SportChip } from "@/components/shared";
import { StaggerGroup, StaggerItem, SportCover, SkeletonGrid } from "@/components/motion";
import { motion } from "framer-motion";
import { hoverLift } from "@/lib/motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["emerald", "orange", "sky", "indigo", "rose", "slate"];
const empty = { name: "", description: "", color: "emerald", cover_image: null };

export default function SportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { const r = await sportsApi.list(); setSports(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s, e) => { e.stopPropagation(); setEditing(s); setForm({ name: s.name, description: s.description || "", color: s.color || "emerald", cover_image: s.cover_image || null }); setOpen(true); };

  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)"); return; }
    try { const cover = await compressImage(file); setForm((p) => ({ ...p, cover_image: cover })); } catch { toast.error("Impossible de traiter l'image"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setBusy(true);
    try {
      if (editing) await sportsApi.update(editing.id, form); else await sportsApi.create(form);
      toast.success(editing ? "Sport mis à jour" : "Sport créé"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <PageContainer>
      <SectionTitle action={user?.role === "admin" && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" onClick={openNew} data-testid="sport-creer-bouton"><Plus className="mr-1.5 h-4 w-4" /> Nouveau sport</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Modifier la section" : "Créer une section sportive"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sport-nom-input" placeholder="Ex : Hockey" /></div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sport-description-input" /></div>
              <div className="space-y-1.5"><Label>Couleur</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{COLORS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Image de couverture</Label>
                <div className="flex items-center gap-3">
                  {form.cover_image ? <img src={form.cover_image} alt="" className="h-16 w-24 rounded-lg object-cover" /> : <div className="flex h-16 w-24 items-center justify-center rounded-lg bg-muted"><ImagePlus className="h-5 w-5 text-muted-foreground" /></div>}
                  <label className="cursor-pointer rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-muted">Téléverser<input type="file" accept="image/*" className="hidden" onChange={onCover} data-testid="sport-cover-input" /></label>
                  {form.cover_image && <button type="button" onClick={() => setForm({ ...form, cover_image: null })} className="text-xs text-rose-600">Retirer</button>}
                </div>
                <p className="text-[11px] text-muted-foreground">Sinon, une image sportive par défaut est utilisée (remplaçable).</p>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={busy} data-testid="sport-enregistrer-bouton">{busy ? "…" : editing ? "Enregistrer" : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}>Sections sportives</SectionTitle>

      {loading ? <SkeletonGrid count={6} /> : sports.length === 0 ? (
        <EmptyState title="Aucun sport" description="Les sections sportives apparaîtront ici." icon={Activity} />
      ) : (
        <StaggerGroup className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sports.map((s) => (
            <StaggerItem key={s.id}>
              <motion.div {...hoverLift}>
                <Card className="group cursor-pointer overflow-hidden" onClick={() => navigate(`/sports/${s.id}`)} data-testid={`sport-carte-${s.name}`}>
                  <SportCover sport={s} className="h-40 w-full">
                    <div className="flex h-40 flex-col justify-between p-4">
                      <div className="flex items-center justify-between">
                        <SportChip name={s.name} color={s.color} className="bg-white/90" />
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.active_members_count > 0 ? "bg-secondary text-white" : "bg-white/80 text-slate-700"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${s.active_members_count > 0 ? "bg-white" : "bg-slate-400"}`} />{s.active_members_count > 0 ? "Active" : "En attente"}
                        </span>
                      </div>
                      <div className="text-white"><div className="font-heading text-xl font-bold drop-shadow">{s.name}</div></div>
                    </div>
                  </SportCover>
                  <div className="p-4">
                    <p className="line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">{s.description || "—"}</p>
                    {s.next_event && (
                      <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-muted/60 px-2.5 py-1.5 text-xs text-foreground"><Calendar className="h-3.5 w-3.5 text-secondary" /> Prochain : {s.next_event.title} · {new Date(s.next_event.start).toLocaleDateString("fr-CA", { day: "numeric", month: "short" })}</div>
                    )}
                    <div className="mt-4 flex items-center justify-between border-t pt-3">
                      <div className="flex items-center gap-2">
                        {s.coach ? <><UserAvatar user={s.coach} size="h-7 w-7" /><span className="text-xs text-muted-foreground">{s.coach.first_name} {s.coach.last_name}</span></> : <span className="text-xs text-muted-foreground">Aucun entraîneur</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> {s.member_count}</span>
                        {user?.role === "admin" && <button onClick={(e) => openEdit(s, e)} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="sport-modifier-bouton"><Pencil className="h-3.5 w-3.5" /></button>}
                        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      )}
    </PageContainer>
  );
}
